import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { lcTimeslotApi, lcEnrollmentApi, Timeslot } from '@/api/lc'
import { tenantApi } from '@/api/tenant'
import { adminDiagnosticsApi } from '@/api/adminDiagnostics'
import { freeTrialApi, type TrialSetCode } from '@/api/freeTrials'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { toast } from 'sonner'
import { UserPlus } from 'lucide-react'

interface Props {
  timeslot: Timeslot
  trialEnabled?: boolean
  onClose: () => void
}

// 결측 필드 표시 (엣지10): em-dash.
const DASH = '—'

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
const STATUS_BADGE: Record<string, string> = {
  WAITING: 'bg-gray-100 text-gray-600',
  WITHDRAWN: 'bg-red-100 text-red-600',
}

interface DaySchedule {
  startTime: string
  durationMinutes: number
}

/**
 * 클래스(timeslot) 원생 배정 모달 — P3.
 * 배정가능 원생 표(BE EP가 SoT, 클라 재필터 금지) + 학습과정(license) 선택 + 진단추천(A~E) 표시(힌트만).
 * 배정 = 기존 lcEnrollmentApi.create 재사용(축1=A). 정원 TIMESLOT_FULL은 BE 차단 유지, 모달은 표시+토스트만(엣지1).
 * 배정(enrollment) ≠ 휴강(ClosureModal)·휴원(on_leave). 반이동(update timeslotId)은 P5라 미노출.
 * 재배정 후보(lcEnrollmentId 有 = 배정 취소된 active 수강): create 대신 update({timeslotId})로
 * 기존 수강에 클래스만 다시 붙인다 — 학습과정은 고정 표시, 무료체험은 잠금.
 */
export default function AssignmentModal({ timeslot, trialEnabled = false, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [licenseId, setLicenseId] = useState<number | undefined>(undefined)
  const [licenseError, setLicenseError] = useState<string | null>(null)
  const [freeTrial, setFreeTrial] = useState(false)
  const [trialSetCode, setTrialSetCode] = useState<TrialSetCode | undefined>(undefined)

  const { data: assignableData } = useQuery({
    queryKey: ['lcAssignable', timeslot.timeslotId],
    queryFn: () => lcTimeslotApi.getAssignableStudents(timeslot.timeslotId),
  })
  const students = assignableData?.data?.data || []

  // 재배정 후보 = 배정 취소된 active 수강 보유(lcEnrollmentId 동봉). 과정 유지·체험 미적용.
  const selectedStudent = students.find((s) => s.userId === selectedUserId)
  const reassignEnrollmentId = selectedStudent?.lcEnrollmentId ?? null
  const isReassign = reassignEnrollmentId != null
  const appliesFreeTrial = trialEnabled && freeTrial && !isReassign

  const { data: licensesData } = useQuery({
    queryKey: ['activeLicenses'],
    queryFn: async () => (await tenantApi.getAllActiveLicenses()).data.data ?? [],
  })
  const licenses = licensesData ?? []

  const { data: trialCatalogData } = useQuery({
    queryKey: ['lcFreeTrialCatalog'],
    queryFn: async () => (await freeTrialApi.catalog()).data.data ?? [],
    enabled: trialEnabled,
  })
  const trialCatalog = trialCatalogData ?? []

  const { data: eligibilityData } = useQuery({
    queryKey: ['lcFreeTrialEligibility', selectedUserId],
    enabled: trialEnabled && selectedUserId != null,
    queryFn: () => freeTrialApi.eligibility(selectedUserId!),
  })
  const trialEligibility = eligibilityData?.data?.data

  // 재적용 참고 정보(spec §10-5): 최근 종결 회차의 세트·소진 여부 — 자격을 막지 않는 안내용
  const { data: trialHistoryData } = useQuery({
    queryKey: ['lcFreeTrialHistory', selectedUserId],
    enabled: trialEnabled && selectedUserId != null,
    queryFn: async () => (await freeTrialApi.history(selectedUserId!)).data.data ?? [],
  })
  const lastTerminatedAttempt = (trialHistoryData ?? [])
    .find((item) => item.status === 'REVOKED' || item.status === 'EXPIRED' || item.status === 'CONVERTED')

  // 진단추천(축4=A): 원생 선택 시 on-demand. row 없음/null/needsAiq → 추천 없음(엣지9). 자동 license 선택 금지.
  // (후보는 항상 신규=active 미보유이므로 "신규=추천 안내"만 의미. "기존=동일과정"은 반이동=P5라 미해당.)
  const { data: recommendation } = useQuery({
    queryKey: ['lcAssignRecommend', selectedUserId],
    enabled: selectedUserId != null,
    queryFn: async () => {
      const listRes = await adminDiagnosticsApi.list()
      const rows = listRes.data?.data || []
      const latest = rows
        .filter((r) => r.userId === selectedUserId && r.status === 'COMPLETED')
        .sort((a, b) => b.roundNo - a.roundNo)[0]
      if (!latest) return { course: null as 'A' | 'B' | 'C' | 'D' | 'E' | null, needsAiq: false }
      const reportRes = await adminDiagnosticsApi.getReport(latest.id)
      const rc = reportRes.data?.data?.recommendedCourse
      return { course: rc?.course ?? null, needsAiq: rc?.needsAiq ?? false }
    },
  })

  useEffect(() => {
    if (!appliesFreeTrial || trialSetCode || !recommendation?.course) return
    if (trialCatalog.some((item) => item.trialSetCode === recommendation.course)) {
      setTrialSetCode(recommendation.course)
    }
  }, [appliesFreeTrial, recommendation?.course, trialCatalog, trialSetCode])

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })
      ?.response?.data?.error?.code
    return code ? t(`apiError.${code}`, { ns: 'common', defaultValue: fallback }) : fallback
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      if (reassignEnrollmentId != null) {
        await lcEnrollmentApi.update(reassignEnrollmentId, { timeslotId: timeslot.timeslotId })
        return
      }
      if (appliesFreeTrial) {
        await freeTrialApi.grant({
          userId: selectedUserId!,
          timeslotId: timeslot.timeslotId,
          trialSetCode: trialSetCode!,
        })
        return
      }
      await lcEnrollmentApi.create({
          userId: selectedUserId!,
          licenseId: licenseId!,
          timeslotId: timeslot.timeslotId,
        })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcEnrollments'] })
      queryClient.invalidateQueries({ queryKey: ['lcTimeslots'] })
      queryClient.invalidateQueries({ queryKey: ['lcRoster', timeslot.timeslotId] })
      queryClient.invalidateQueries({ queryKey: ['lcAssignable', timeslot.timeslotId] })
      queryClient.invalidateQueries({ queryKey: ['lcFreeTrials', timeslot.timeslotId] })
      queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatuses'] })
      toast.success(t(appliesFreeTrial ? 'assignment.trial.toast.success' : 'assignment.toast.success'))
      onClose()
    },
    onError: (err) => toast.error(apiErrorMessage(err, t('assignment.toast.failed'))),
  })

  const handleSubmit = () => {
    if (selectedUserId == null) return
    if (isReassign) {
      // 재배정: 기존 수강 유지 → license/체험 검증 없이 클래스만 배정
      setLicenseError(null)
      createMutation.mutate()
      return
    }
    if (appliesFreeTrial) {
      if (!trialEligibility?.eligible) {
        setLicenseError(t('assignment.trial.validation.ineligible'))
        return
      }
      if (!trialSetCode) {
        setLicenseError(t('assignment.trial.validation.setRequired'))
        return
      }
      setLicenseError(null)
      createMutation.mutate()
      return
    }
    if (licenseId == null) {
      setLicenseError(t('assignment.validation.licenseRequired'))
      return
    }
    setLicenseError(null)
    createMutation.mutate()
  }

  const genderLabel = (gender: string | null): string =>
    gender ? t(`assignment.gender.${gender}`, { defaultValue: gender }) : DASH

  const statusLabel = (status: string): string =>
    t(`students.memberStatus.${status}`, { defaultValue: status })

  const parseSchedule = (): Partial<Record<(typeof DAY_KEYS)[number], DaySchedule>> => {
    let parsed: Partial<Record<(typeof DAY_KEYS)[number], DaySchedule>>
    try {
      parsed = JSON.parse(timeslot.schedule) || {}
    } catch {
      parsed = {}
    }
    return parsed
  }

  const scheduleEntries = () => {
    const parsed = parseSchedule()
    return DAY_KEYS.filter((d) => parsed[d]).map((d) => ({ day: d, ...parsed[d]! }))
  }

  const scheduleDays = (): string => {
    const entries = scheduleEntries()
    if (entries.length === 0) return t('assignment.classInfo.scheduleNone')
    return entries.map((entry) => t(`timeslot.days.${entry.day}`)).join(', ')
  }

  const scheduleStartTimes = (): string => {
    const entries = scheduleEntries()
    if (entries.length === 0) return DASH
    return entries.map((entry) => `${t(`timeslot.days.${entry.day}`)} ${entry.startTime}`).join(', ')
  }

  const scheduleDurations = (): string => {
    const entries = scheduleEntries()
    if (entries.length === 0) return DASH
    return entries.map((entry) => `${t(`timeslot.days.${entry.day}`)} ${entry.durationMinutes}${t('assignment.classInfo.minuteUnit')}`).join(', ')
  }

  const weeklyCount = (): number => {
    return scheduleEntries().length
  }

  const scheduleSummary = (): string => {
    const parsed = parseSchedule()
    const entries = DAY_KEYS.filter((d) => parsed[d])
    if (entries.length === 0) return t('assignment.classInfo.scheduleNone')
    return entries
      .map((d) => `${t(`timeslot.days.${d}`)} ${parsed[d]!.startTime}`)
      .join(', ')
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            {t('assignment.title', { name: timeslot.name })}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500">{t('assignment.description')}</p>

        {/* 클래스 정보 패널 */}
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 space-y-1">
          <div>{t('assignment.classInfo.name')}: {timeslot.name}</div>
          <div>{t('assignment.classInfo.weeklyCount')}: {t('timeslot.weeklyCount', { n: weeklyCount() })}</div>
          <div>{t('assignment.classInfo.days')}: {scheduleDays()}</div>
          <div>{t('assignment.classInfo.startTime')}: {scheduleStartTimes()}</div>
          <div>{t('assignment.classInfo.duration')}: {scheduleDurations()}</div>
          <div>{t('assignment.classInfo.studentCount')}: {t('assignment.classInfo.studentCountValue', { current: timeslot.currentStudents })}</div>
          <div>{t('assignment.classInfo.schedule')}: {scheduleSummary()}</div>
        </div>

        {/* 배정가능 원생 표 (축2 EP가 SoT) */}
        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-gray-200">
          {students.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">{t('assignment.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.name')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.status')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.handle')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.gradeLevel')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.gender')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('assignment.table.guardianPhone')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {students.map((s) => (
                  <tr
                    key={s.userId}
                    data-testid={`assignable-row-${s.userId}`}
                    onClick={() => setSelectedUserId(s.userId)}
                    aria-selected={selectedUserId === s.userId}
                    className={`cursor-pointer hover:bg-gray-50 ${selectedUserId === s.userId ? 'bg-primary-50' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="assignable-student"
                          checked={selectedUserId === s.userId}
                          onChange={() => setSelectedUserId(s.userId)}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="font-medium text-gray-900">{s.fullName}</span>
                      </label>
                    </td>
                    <td className="px-3 py-2">
                      {s.lcEnrollmentId != null ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {t('assignment.unassignedBadge')}
                        </span>
                      ) : (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[s.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel(s.status)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{s.handle || DASH}</td>
                    <td className="px-3 py-2 text-gray-600">{s.gradeLevel || DASH}</td>
                    <td className="px-3 py-2 text-gray-600">{genderLabel(s.gender)}</td>
                    <td className="px-3 py-2 text-gray-600">{s.guardianPhone || DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 진단 추천(힌트만) — 원생 선택 시 표시 */}
        {selectedUserId != null && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-500">{t('assignment.recommend.label')}:</span>
            {recommendation?.course ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                {t('assignment.recommend.course', { course: recommendation.course })}
              </span>
            ) : recommendation?.needsAiq ? (
              <span className="text-gray-400">{t('assignment.recommend.needsAiq')}</span>
            ) : (
              <span className="text-gray-400">{t('assignment.recommend.none')}</span>
            )}
          </div>
        )}

        {trialEnabled ? (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <label className="flex items-center justify-between gap-3">
              <span>
                <span className="block text-sm font-semibold text-emerald-900">{t('assignment.trial.label')}</span>
                <span className="block text-xs text-emerald-700">{t('assignment.trial.help')}</span>
              </span>
              <input
                type="checkbox"
                data-testid="assignment-free-trial"
                checked={freeTrial && !isReassign}
                disabled={selectedUserId == null || isReassign || trialEligibility?.eligible === false}
                onChange={(e) => {
                  setFreeTrial(e.target.checked)
                  setLicenseError(null)
                  if (!e.target.checked) setTrialSetCode(undefined)
                }}
                className="h-5 w-5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
              />
            </label>
            {selectedUserId != null && trialEligibility?.eligible === false && (
              <p className="mt-2 text-xs font-medium text-red-600">
                {t(`assignment.trial.reason.${trialEligibility.reason}`, { defaultValue: t('assignment.trial.validation.ineligible') })}
              </p>
            )}
            {selectedUserId != null && lastTerminatedAttempt && (
              <p className="mt-2 text-xs text-emerald-800" data-testid="assignment-trial-last-attempt">
                {t(
                  lastTerminatedAttempt.consumed
                    ? 'assignment.trial.lastAttempt.consumed'
                    : 'assignment.trial.lastAttempt.unused',
                  {
                    set: lastTerminatedAttempt.trialSetCode,
                    status: t(`assignment.trial.attemptStatus.${lastTerminatedAttempt.status}`),
                  },
                )}
              </p>
            )}
            {appliesFreeTrial && (
              <div className="mt-3">
                <label className="mb-1 block text-sm font-medium text-emerald-900">
                  {t('assignment.trial.setLabel')} *
                </label>
                <select
                  data-testid="assignment-trial-set"
                  value={trialSetCode ?? ''}
                  onChange={(e) => {
                    setTrialSetCode(e.target.value as TrialSetCode || undefined)
                    setLicenseError(null)
                  }}
                  className="w-full rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">{t('assignment.trial.setPlaceholder')}</option>
                  {trialCatalog.map((item) => (
                    <option key={item.trialSetCode} value={item.trialSetCode}>
                      {t('assignment.trial.setOption', { set: item.trialSetCode, name: item.productName })}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ) : null}

        {/* 재배정 후보: 학습과정 고정 표시(기존 수강 유지, 클래스만 배정) */}
        {isReassign && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('assignment.license.label')}
            </label>
            <div
              data-testid="assignment-license-fixed"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700"
            >
              {selectedStudent?.productName || DASH}
            </div>
            <p className="mt-1 text-xs text-gray-500">{t('assignment.license.reassignHelp')}</p>
          </div>
        )}

        {/* 학습과정(license) 선택 — 필수(엣지4) */}
        {!appliesFreeTrial && !isReassign && <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('assignment.license.label')} *
          </label>
          <select
            data-testid="assignment-license"
            value={licenseId ?? ''}
            onChange={(e) => {
              setLicenseId(e.target.value ? Number(e.target.value) : undefined)
              setLicenseError(null)
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">{t('assignment.license.placeholder')}</option>
            {licenses.map((l) => (
              <option key={l.id} value={l.id}>
                {l.productName || l.curriculumTitle || `#${l.productId}`}
              </option>
            ))}
          </select>
          {licenseError && <p className="mt-1 text-xs text-red-600">{licenseError}</p>}
        </div>}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('button.cancel', { ns: 'common' })}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={selectedUserId == null || createMutation.isPending || (appliesFreeTrial && (!trialEligibility?.eligible || !trialSetCode))}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {t('assignment.submit')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
